import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { notificationsAPI } from './api';

// Pure: map a push's custom data to an in-app route. Exported for testing.
export function routeForPush(data = {}) {
  if (data.type === 'friend_request') return '/friends';
  if (
    (data.type === 'discussion_reply' || data.type === 'comment_reply') &&
    data.book_id &&
    data.discussion_id
  ) {
    return `/books/${data.book_id}/discussions/${data.discussion_id}`;
  }
  return '/notifications';
}

export function isSupported() {
  return Capacitor.isNativePlatform();
}

// Returns the last device token this app registered with the backend, if any.
export function getStoredToken() {
  return localStorage.getItem('pushToken');
}

const REG_ERROR_KEY = 'pushRegError';

// The human-readable reason the last registration attempt failed (or null).
// Persists so Settings can show it even after a navigation/remount.
export function getLastRegError() {
  return localStorage.getItem(REG_ERROR_KEY);
}

function apnsErrorText(err) {
  return (err && (err.error || err.message)) || 'Unknown push registration error';
}

// Single source of truth for a registration attempt's result. Broadcasts a
// 'push:result' event so both enable() and the Settings screen can react, and
// persists any failure reason so it survives a remount.
function emitResult(result) {
  if (result.ok) {
    localStorage.removeItem(REG_ERROR_KEY);
  } else {
    localStorage.setItem(REG_ERROR_KEY, result.detail || 'Push registration failed.');
  }
  window.dispatchEvent(new CustomEvent('push:result', { detail: result }));
}

// Whether push is actually on for this device: OS permission granted AND this
// device has a token registered with the backend.
export async function isEnabled() {
  if (!isSupported()) return false;
  const perm = await PushNotifications.checkPermissions();
  return perm.receive === 'granted' && !!getStoredToken();
}

// Request permission + register this device. Resolves to a structured result
// describing the ACTUAL outcome ({ ok, code, detail }) — it waits for Apple to
// deliver a token (or an error) rather than returning as soon as register() is
// called, so callers can surface the real reason a device didn't register.
export async function enable() {
  if (!isSupported()) return { ok: false, code: 'unsupported', detail: 'Not supported on this platform.' };
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    return {
      ok: false,
      code: 'permission',
      detail: 'Enable notifications for Dog-Ear in iOS Settings.',
    };
  }
  // Wait for the registration / registrationError event (handled by the
  // app-lifetime listeners in initListeners, which emit 'push:result').
  const outcome = new Promise((resolve) => {
    const onResult = (ev) => {
      window.removeEventListener('push:result', onResult);
      clearTimeout(timer);
      resolve(ev.detail);
    };
    const timer = setTimeout(() => {
      window.removeEventListener('push:result', onResult);
      resolve({
        ok: false,
        code: 'timeout',
        detail: 'No response from Apple Push service (timed out). Try again.',
      });
    }, 10000);
    window.addEventListener('push:result', onResult);
  });
  try {
    await PushNotifications.register();
  } catch (e) {
    emitResult({ ok: false, code: 'register-threw', detail: apnsErrorText(e) });
  }
  return outcome;
}

export async function disable(token) {
  if (!isSupported()) return;
  const t = token || getStoredToken();
  if (t) await notificationsAPI.unregisterDevice(t).catch(() => {});
  localStorage.removeItem('pushToken');
  localStorage.removeItem(REG_ERROR_KEY);
  // NB: the app-lifetime listeners (registration / tap) are intentionally left
  // in place so re-enabling in the same session still registers a token.
}

// Wire listeners once for the app's lifetime (registration → backend; tap →
// navigate). Idempotent: safe against StrictMode double-invocation / re-mounts.
let _listenersAdded = false;
export function initListeners(navigate) {
  if (!isSupported() || _listenersAdded) return;
  _listenersAdded = true;
  PushNotifications.addListener('registration', async (t) => {
    localStorage.setItem('pushToken', t.value);
    try {
      await notificationsAPI.registerDevice(t.value);
      emitResult({ ok: true, code: 'registered' });
    } catch (e) {
      // Token issued by Apple but the backend rejected/never received it
      // (e.g. an expired login → 401). Don't leave a stored token that would
      // make the toggle lie about being "on".
      localStorage.removeItem('pushToken');
      const status = e && e.response && e.response.status;
      emitResult({
        ok: false,
        code: 'server',
        detail: status
          ? `Apple issued a token but the server rejected it (HTTP ${status}). Make sure you're logged in and try again.`
          : 'Apple issued a token but it could not be sent to the server (network error).',
      });
    }
  });
  PushNotifications.addListener('registrationError', (err) => {
    localStorage.removeItem('pushToken');
    emitResult({ ok: false, code: 'apns', detail: `Apple refused push registration: ${apnsErrorText(err)}` });
  });
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action?.notification?.data || {};
    navigate(routeForPush(data));
  });
}
